import React, { useState } from 'react'
import {
  Container,
  TextBody,
  TextCaption,
  Icon,
  Button,
  Expandable,
  Select,
  FieldText,
  Image,
  ListContainer
} from '@pmi/pmi-iqos-ds-react'
import { htmlTranslation, Assets } from '@pmi/pmi-iqos-shared-react'

const CheckoutPickup = ({
  translations,
  pickupRegions,
  defaultZipCode,
  handleSearchPickupStores,
  pickupStoreList,
  pickupStoreListNumber,
  handleSelectPickupStore,
  selectedPickupStore,
  pickupStoreListNoResult
}) => {
  const [pickupZipCode, setPickupZipCode] = useState(defaultZipCode || '')
  const [pickupSelectedRegion, setPickupSelectedRegion] = useState('')
  const [pickupSelectedArea, setPickupSelectedArea] = useState('')
  const [pickupStoreCurrentListNumber, setPickupStoreCurrentListNumber] =
    useState(pickupStoreListNumber)

  const pickupRegionsItems = [
    {
      value: '',
      text: translations.tk_checkoutShippingFSSSearchSelectRegion
    }
  ].concat(
    pickupRegions.map(el => ({
      value: el.regionId,
      text: el.regionName
    }))
  )
  const region =
    pickupRegions &&
    pickupRegions.find(el => el.regionId === pickupSelectedRegion)
  const areas = (region && region.areas) || []

  const pickupAreasItems = [
    {
      value: '',
      text: translations.tk_checkoutShippingFSSSearchSelectArea
    }
  ].concat(
    areas.map(el => ({
      value: el.areaId,
      text: el.areaName
    }))
  )

  const pickupStores = pickupStoreList.slice(0, pickupStoreCurrentListNumber)
  const half = Math.ceil(pickupStores.length / 2)
  const cut = Math.min(half, pickupStores.length)
  const firstPickupStoresHalf = pickupStores.slice(0, cut)
  const secondPickupStoresHalf = pickupStores.slice(cut, pickupStores.length)

  const handleSearchPickupStoreClick = async currentLocation => {
    let params = null

    if (currentLocation && currentLocation.coords) {
      params = {
        location: {
          lat: currentLocation.coords.latitude.toFixed(6),
          lng: currentLocation.coords.longitude.toFixed(6)
        }
      }
    } else if (pickupSelectedRegion && pickupSelectedArea) {
      params = {
        area: {
          regionId: pickupSelectedRegion,
          areaId: pickupSelectedArea
        }
      }
    } else if (pickupZipCode) {
      params = {
        postalCode: pickupZipCode
      }
    }

    if (!params) {
      return
    }

    setPickupStoreCurrentListNumber(pickupStoreListNumber)
    handleSearchPickupStores && handleSearchPickupStores(params)
  }

  const handleUseCurrentLocationClick = () => {
    if (navigator.geolocation) {
      setPickupSelectedRegion('')
      setPickupSelectedArea('')
      setPickupZipCode('')
      navigator.geolocation.getCurrentPosition(position => {
        handleSearchPickupStoreClick(position)
      })
    }
  }

  const formatPickupStoreDistance = distance => {
    const rounded = (Math.round(distance * 100) / 100).toFixed(2)

    const isInKm = distance > 1
    const kmHasVar =
      translations.tk_checkoutPickStoreListDistanceKm.indexOf(
        '[[ location_distance ]]'
      ) > -1
    const mtHasVar =
      translations.tk_checkoutPickStoreListDistanceMeters.indexOf(
        '[[ location_distance ]]'
      ) > -1

    return isInKm
      ? `${
          kmHasVar ? '' : rounded
        }${translations.tk_checkoutPickStoreListDistanceKm.replace(
          '[[ location_distance ]]',
          rounded
        )}`
      : `${
          mtHasVar ? '' : rounded * 1000
        }${translations.tk_checkoutPickStoreListDistanceMeters.replace(
          '[[ location_distance ]]',
          rounded * 1000
        )}`
  }

  const handleShowMorePickupStoreClick = () => {
    setPickupStoreCurrentListNumber(
      pickupStoreCurrentListNumber + pickupStoreListNumber
    )
  }

  const renderPickupStore = (store, index) => {
    return (
      <Container
        key={index}
        display={Container.displays.flex}
        borderColor={
          store.ieCode === selectedPickupStore
            ? Container.borderColors.primary
            : Container.borderColors.default
        }
        flexDirection={Container.flexDirections.column}
        p="4"
        onClickHandler={() => {
          handleSelectPickupStore(store.ieCode)
        }}>
        <Container
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.row}
          justifyContent={Container.justifyContentValues.between}
          alignItems={Container.alignItemsValues.center}>
          <Container>
            <TextCaption
              size={TextBody.sizes.s}
              data-testing="Checkout_TextCaption_StoreName">
              {store.baseName}
            </TextCaption>
            <TextBody
              size={TextBody.sizes.s}
              data-testing="Checkout_TextBody_StoreCity">
              {[store.local, store.city].join(' ')}
            </TextBody>
          </Container>
          <Container width="10">
            <Image
              width="16"
              src={Assets.buildUrl(
                Assets.variableUrls.iconPickupStore,
                store.shopType
              )}
            />
          </Container>
        </Container>
        <Container>
          <Expandable isOpen={store.ieCode === selectedPickupStore}>
            <Container pt="4" divider={Container.dividers.bottom} />
            <Container pt="6">
              <Container
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.row}
                justifyContent={Container.justifyContentValues.between}
                alignItems={Container.alignItemsValues.start}>
                <Container display={Container.displays.flex}>
                  <Container pr="3">
                    <Icon size={Icon.sizes.xs} image={Icon.images.time} />
                  </Container>
                  <TextBody
                    size={TextBody.sizes.s}
                    {...htmlTranslation(
                      translations,
                      'tk_checkoutPickStoreListBusinessHours'
                    )}
                  />
                </Container>
                <Container>
                  {store.businessHours && (
                    <Container>
                      {Object.keys(store.businessHours).map(key => (
                        <Container
                          key={key}
                          display={Container.displays.flex}
                          justifyContent={Container.justifyContentValues.end}>
                          <TextBody
                            size={TextBody.sizes.s}
                            data-testing="Checkout_TextBody_StoreOpening">
                            {store.businessHours[key]}
                          </TextBody>
                        </Container>
                      ))}
                    </Container>
                  )}
                </Container>
              </Container>
            </Container>
            <Container pt="4">
              <Container
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.row}
                justifyContent={Container.justifyContentValues.between}
                alignItems={Container.alignItemsValues.center}>
                <Container display={Container.displays.flex}>
                  <Container pr="3">
                    <Icon size={Icon.sizes.xs} image={Icon.images.location} />
                  </Container>
                  <TextBody
                    size={TextBody.sizes.s}
                    {...htmlTranslation(
                      translations,
                      'tk_checkoutPickStoreListBusinessLocation'
                    )}
                  />
                </Container>
                <Container>
                  {store.distance && (
                    <TextBody
                      size={TextBody.sizes.s}
                      data-testing="Checkout_TextBody_StoreDistance">
                      {formatPickupStoreDistance(store.distance)}
                    </TextBody>
                  )}
                </Container>
              </Container>
            </Container>
          </Expandable>
        </Container>
      </Container>
    )
  }

  return (
    <Container>
      <Container px={['0', '4', '8']} py="8">
        <Container
          display={Container.displays.flex}
          alignItems={Container.alignItemsValues.center}
          justifyContent={Container.justifyContentValues.center}
          flexDirection={[
            Container.flexDirections.column,
            Container.flexDirections.row
          ]}
          pb={['6', '8', '8']}>
          <Container
            width={['full', '5/12']}
            display={Container.displays.flex}
            flexDirection={[
              Container.flexDirections.row,
              Container.flexDirections.column
            ]}
            pb={['6', '0']}>
            <Container pb={['0', '5']} pr={['4', '0']} width={['1/2', 'full']}>
              <Select
                name="pickupRegion"
                label=""
                value={pickupSelectedRegion}
                items={pickupRegionsItems}
                getItemLabel={index => pickupRegionsItems[index].text}
                getItemValue={index => pickupRegionsItems[index].value}
                onChangeHandler={e => {
                  setPickupSelectedRegion(e.target.value)
                  setPickupSelectedArea('')
                  setPickupZipCode('')
                }}
                data-testing="Checkout_Select_PickupRegion"
              />
            </Container>
            <Container width={['1/2', 'full']}>
              <Select
                name="pickupArea"
                label=""
                value={pickupSelectedArea}
                items={pickupAreasItems}
                getItemLabel={index => pickupAreasItems[index].text}
                getItemValue={index => pickupAreasItems[index].value}
                onChangeHandler={e => {
                  setPickupSelectedArea(e.target.value)
                  setPickupZipCode('')
                }}
                data-testing="Checkout_Select_PickupArea"
              />
            </Container>
          </Container>
          <Container
            width={['full', '2/12']}
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.center}
            alignItems={Container.alignItemsValues.center}
            flexDirection={[
              Container.flexDirections.row,
              Container.flexDirections.column
            ]}>
            <Container
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}
              pb={['0', '2']}
              pr={['2', '0']}>
              <Container
                bg={Container.backgrounds.border}
                width={['full', 'px']}
                height={['px', '10']}
              />
            </Container>
            <Container>
              <TextBody
                size={TextBody.sizes.s}
                style={TextBody.styles.grayedout}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutShippingFSSSearchOR'
                )}
              />
            </Container>
            <Container
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}
              pt={['0', '2']}
              pl={['2', '0']}>
              <Container
                bg={Container.backgrounds.border}
                width={['full', 'px']}
                height={['px', '10']}
              />
            </Container>
          </Container>
          <Container width={['full', '5/12']} pt={['6', '0']}>
            <FieldText
              name="pickupZipCode"
              label={translations.tk_checkoutShippingFSSSearchByPostCode}
              value={pickupZipCode}
              onChangeHandler={e => {
                setPickupSelectedRegion('')
                setPickupSelectedArea('')
                setPickupZipCode(e.target.value)
              }}
              icon={Icon.images.search}
              data-testing="Checkout_FieldText_PickupZipCode"
            />
          </Container>
        </Container>
        <Container divider={Container.dividers.bottom} />
        <Container
          pt={['6', '8', '8']}
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.center}
          alignItems={Container.alignItemsValues.center}
          flexDirection={Container.flexDirections.column}>
          <Container>
            <Button
              variant={Button.variants.secondary}
              onClickHandler={() => handleSearchPickupStoreClick(null)}
              text={translations.tk_checkoutShippingFSSSearchButton}
              data-testing="Checkout_Button_SearchPickupStore"
            />
          </Container>
          <Container pt="6">
            <Button
              variant={Button.variants.tertiary}
              onClickHandler={handleUseCurrentLocationClick}
              text={translations.tk_checkoutShippingFSSSearchUseCurrentLocation}
              icon={Icon.images.location}
              iconPosition={Button.iconPositions.right}
              data-testing="Checkout_Button_UseCurrentLocation"
            />
          </Container>
        </Container>
      </Container>
      {(pickupStores.length > 0 || pickupStoreListNoResult) && (
        <Container pt={['10', '8']}>
          <Container pb={['5', '0']}>
            <TextBody
              size={TextBody.sizes.l}
              {...htmlTranslation(
                translations,
                'tk_checkoutPickStoreListTitle'
              )}
            />
          </Container>
          <Container
            divider={Container.dividers.bottom}
            display={[Container.displays.block, Container.displays.none]}
          />
          {pickupStoreListNoResult ? (
            <Container pt="6">
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutGetPickupSoreListNoResult'
                )}
              />
            </Container>
          ) : (
            <Container fullWidth pt="6">
              <ListContainer columns={[1, 2]} yBetween={['6', '8']}>
                <Container fullWidth pr={['0', '4']}>
                  <ListContainer columns={1} yBetween={['6', '8']}>
                    {firstPickupStoresHalf.map((store, index) =>
                      renderPickupStore(store, index)
                    )}
                  </ListContainer>
                </Container>
                <Container fullWidth pl={['0', '4']}>
                  <ListContainer columns={1} yBetween={['6', '8']}>
                    {secondPickupStoresHalf.map((store, index) =>
                      renderPickupStore(store, index)
                    )}
                  </ListContainer>
                </Container>
              </ListContainer>
              {pickupStoreCurrentListNumber < pickupStoreList.length && (
                <Container
                  pt="6"
                  display={Container.displays.flex}
                  justifyContent={Container.justifyContentValues.center}>
                  <Button
                    variant={Button.variants.tertiary}
                    onClickHandler={handleShowMorePickupStoreClick}
                    text={translations.tk_checkoutPickStoreListShowMore}
                    data-testing="Checkout_Button_ShowMorePickupStores"
                  />
                </Container>
              )}
            </Container>
          )}
        </Container>
      )}
    </Container>
  )
}

export default CheckoutPickup
